import type { HTMLAttributes } from "react";
import React from "react";
import styled from "styled-components";

import { isDefined } from "../../../../../shared/utils/assert";
import { useRTL } from "../../../../domain/language/use-rtl";
import { UnstyledButton } from "../../../common/buttons/default-button";
import { PenIcon } from "../../../common/svg/pen-icon";
import { theme } from "../../../styles/theme";

interface InformationSectionProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  value?: string;
  isPhone?: boolean;
  children?: React.ReactNode;
  onEdit?: () => void;
}

/** A line of the profile: its label, then the value in a box of its own, as a field would read. */
export const InformationSection = (props: InformationSectionProps) => {
  const { children, label, value, isPhone, onEdit, ...rest } = props;
  const { isRTL } = useRTL();

  if (!isDefined(value)) {
    return null;
  }
  return (
    <InformationContainer {...rest}>
      <LabelRow>
        <InformationLabel>{label}</InformationLabel>
        {!!onEdit && (
          <EditButton type="button" aria-label={label} onClick={onEdit}>
            <PenIcon width={16} height={16} fill={theme.colors.blue[700]} />
          </EditButton>
        )}
      </LabelRow>
      {children ? (
        children
      ) : (
        /* What the holder may change reads in full; the rest is settled, and stays quiet. */
        <InformationValue $isRTL={isRTL} $isPhone={isPhone} $editable={!!onEdit}>
          {value}
        </InformationValue>
      )}
    </InformationContainer>
  );
};

const InformationContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const LabelRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const InformationLabel = styled.span`
  ${theme.text};
  font-size: 14px;
  line-height: 18px;
  color: ${theme.colors.blue[800]};
`;

const EditButton = styled(UnstyledButton)`
  display: flex;
  padding: 2px;
`;

const InformationValue = styled.span<{ $isRTL: boolean; $isPhone?: boolean; $editable?: boolean }>`
  ${theme.text};
  padding: 12px 16px;
  border: 1px solid ${({ $editable }) => ($editable ? theme.colors.gray[500] : theme.colors.gray[300])};
  border-radius: 8px;
  background-color: #ffffff;
  font-size: 15px;
  line-height: 20px;
  text-align: ${(props) => (props.$isRTL ? "right" : "left")};
  color: ${({ $editable }) => ($editable ? theme.colors.blue[800] : theme.colors.gray[500])};
  direction: ${(props) => (!props.$isPhone && props.$isRTL ? "rtl" : "ltr")};
  overflow-wrap: anywhere;
`;
